import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, ArrowRight, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Register() {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings/public`).then(res => {
      if (!cancelled) {
        setRegistrationEnabled(res.data.registration_enabled !== false);
        setSettingsLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setRegistrationEnabled(true); // fail-open if endpoint unreachable
        setSettingsLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('passwords do not match'); return; }
    if (password.length < 6) { toast.error('min 6 characters'); return; }
    if (!email.toLowerCase().endsWith('@gmail.com')) { toast.error(t('admin.gmail_only')); return; }
    setLoading(true);
    try {
      await register(email, password);
      toast.success(t('auth.verify_code_sent'));
      navigate('/verify-email', { state: { email } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && typeof detail === 'string' && detail.toLowerCase().includes('disabled')) {
        setRegistrationEnabled(false);
        toast.error(t('auth.registration_disabled_title'));
      } else {
        toast.error(detail || 'registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="register-page">
      <Navbar />
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="text-[10px] text-primary tracking-wider mb-4">$ auth --register</div>
          <h1 className="text-xl font-bold tracking-tight mb-1" data-testid="register-title">{t('auth.register_title')}</h1>
          <p className="text-[11px] text-muted-foreground mb-8">{t('auth.register_subtitle')}</p>

          {settingsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : !registrationEnabled ? (
            <div
              className="rounded border border-border/50 bg-card/60 p-5 text-center space-y-3"
              data-testid="registration-disabled-card"
            >
              <Lock className="h-7 w-7 mx-auto text-primary/70" />
              <h2 className="font-heading text-sm font-bold">{t('auth.registration_disabled_title')}</h2>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {t('auth.registration_disabled_msg')}
              </p>
              <div className="pt-2">
                <GoogleSignInButton />
              </div>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[10px] text-muted-foreground tracking-wider">{t('auth.email')}</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@gmail.com" required dir="ltr" data-testid="register-email-input"
                    className="h-10 rounded bg-card border-border/50 text-xs" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] text-muted-foreground tracking-wider">{t('auth.password')}</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="********" required dir="ltr" data-testid="register-password-input"
                    className="h-10 rounded bg-card border-border/50 text-xs" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] text-muted-foreground tracking-wider">{t('auth.confirm_password')}</Label>
                  <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="********" required dir="ltr" data-testid="register-confirm-password-input"
                    className="h-10 rounded bg-card border-border/50 text-xs" />
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit-btn"
                  className="w-full h-10 rounded text-[11px] tracking-wider">
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>sign up <ArrowRight className="ms-1.5 h-3 w-3" /></>}
                </Button>
              </form>

              <div className="mt-4">
                <GoogleSignInButton />
              </div>
            </>
          )}

          <p className="text-[11px] text-muted-foreground mt-6 text-center">
            {t('auth.has_account')}{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">{t('auth.login_link')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
